import { Input } from 'antd';
import Search from 'antd/es/transfer/search';
import HeaderBreadcrumb from '../../../components/Breadcrumb';
import {useEffect, useMemo, useState} from 'react';
import { HttpRes } from '../../../core/dtos/httpRes';
import Loader from '../../../components/Loader';
import formatDate from '../../../utils/formatDate';
import Pagination from 'antd/lib/pagination';
import { RequestFilter } from '../../../core/types/RequestFilter';
import { CountryRes } from '../../../core/dtos/countryRes';
import { CountryReq } from '../../../core/dtos/countryReq';
import { createCountry, deleteCountry, getCountries, updateCountry } from '../../../services/countryService';
import {debounce} from 'lodash';
import createPageTitle from '../../../utils/createPageTitle';
import {Helmet} from 'react-helmet';
import getDateFromDatePicker from '../../../utils/getDateFromDatePicker';
import {
  Row,
  Form,
  Col,
  Card,
  Table,
  Button,
  Tooltip,
  DatePicker,
  Popconfirm,
  message,
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  DeleteOutlined
} from '@ant-design/icons';

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: '36%'
  },
  {
    title: 'Created At',
    dataIndex: 'createdAt',
    key: 'createdAt',
  },
  {
    title: '',
    dataIndex: 'actions',
    key: 'actions',
  },
];

const generateFilterParams = (filter: RequestFilter): RequestFilter => ({
  limit: filter.limit || undefined,
  name: filter.name || undefined,
  page: filter.page || undefined,
  fromCreatedAt: filter.fromCreatedAt || undefined,
  toCreatedAt: filter.toCreatedAt || undefined,
});

function Countries() {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [countries, setCountries] = useState<HttpRes<CountryRes>>({
    items: [],
    pagination: {
      count: 0,
      current: 0,
      hasNext: false,
      hasPrev: false,
      itemCount: 0,
      next: null,
      prev: null
    },
    totalItemCount: 0
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [edit, setEdit] = useState<CountryReq>({
    _id: '',
    name: '',
  });
  const [filter, setFilter] = useState<RequestFilter>({
    limit: 20,
    page: 1,
    fromCreatedAt: '',
    toCreatedAt: '',
  });

  const changeFilter = (changedFilter: Partial<RequestFilter>) => {
    if (!changedFilter.page) {
      setFilter({
        ...filter,
        page: 1
      });
    }
    setFilter((filter) => ({
      ...filter,
      ...changedFilter
    }));
  };

  const mapCategories = () => countries.items.map(country => (
    {
      key: country._id,
      name: (
        <b>{country.name}</b>
      ),
      createdAt: (
        <span>{formatDate(country.createdAt)}</span>
      ),
      actions: (
        <>
          <Row className="ant-employed" gutter={[8, 0]} style={{justifyContent: 'flex-end'}}>
            <Col>
              <Tooltip title="Edit">
                <a href="#form">
                  <Button type="primary" shape="circle" icon={<EditOutlined />} onClick={() => {setEdit({_id: country._id, name: country.name}), form.setFieldValue('name', country.name);}} />
                </a>
              </Tooltip>
            </Col>
            <Col>
              <Popconfirm
                placement="bottomRight"
                title='Delete the Category'
                description='Are you sure to delete this category?'
                onConfirm={() => {
                  deleteCountry(country._id)
                    .then(() => {
                      handleGetCountries();
                    });
                }}
                okText="Delete"
                cancelText="Cancel"
              >
                <Tooltip title="Delete">
                  <Button type="primary" shape="circle" icon={<DeleteOutlined />} danger />
                </Tooltip>
              </Popconfirm>
            </Col>
          </Row>
        </>
      ),
    }
  ));

  const handleGetCountries = () => {
    const filterParams: RequestFilter = generateFilterParams(filter);

    getCountries(filterParams)
      .then((body: HttpRes<CountryRes>) => setCountries(body))
      .finally(() => setIsLoading(false));
  };

  const handleSubmit = () => {
    if (edit._id !== '') {
      updateCountry({...form.getFieldsValue(), _id: edit._id })
        .then(() => {
          handleGetCountries();
          setEdit({
            name: '',
            _id: '',
          });
          form.setFieldValue('name', '');
          messageApi.open({
            type: 'success',
            content: 'Country successfully updated',
          });
        });
    } else {
      createCountry(form.getFieldsValue())
        .then(() => {
          handleGetCountries();
          form.setFieldValue('name', '');
          messageApi.open({
            type: 'success',
            content: 'Country successfully created',
          });
        });
    }
  };

  const handleSearchWithDebounce = useMemo(() => {
    return debounce(
      (e) => changeFilter({ name: e.target.value }),
      500
    );
  }, []);

  useEffect(() => {
    handleGetCountries();
  }, [filter]);

  if (isLoading) {
    return (
      <Loader />
    );
  }
  return (
    <>
      <Helmet>
        <title>{createPageTitle('Countries')}</title>
      </Helmet>
      {contextHolder}
      <HeaderBreadcrumb items={['Countries']} />
      <div className="tabled">
        <Row gutter={[24, 16]}>
          <Col xs={24}>
            <Form name='Country' form={form} onFinish={handleSubmit} layout='vertical' id='form'>
              <Card
                bordered={false}
                className="criclebox tablespace mb-24"
                title="Create Country"
                extra={
                  <Button type="primary" htmlType='submit' shape="default" icon={<SaveOutlined />} size="large" style={{width: '120px'}}>
                    {edit._id !== '' ? 'Save' : 'Create'}
                  </Button>
                }
              >
                <div className="table-responsive">
                  <Row gutter={[24, 24]}>
                    <Col xs={24} style={{gap: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <Form.Item
                        name='name'
                        rules={[{ required: true, message: 'Please enter country name' }, { min: 3, message: 'Country name must be longer than 2 characters' }]}
                        label='Country Name'
                        style={{width: '100%'}}
                      >
                        <Input placeholder="Country Name" size='large' />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Card>
            </Form>
          </Col>
          <Col xs={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="List of Countries"
              extra={
                <Row className='table-filter'>
                  <Col xs={12} style={{marginRight: '10px'}}>
                    <Search placeholder="Search in country name" onChange={handleSearchWithDebounce} />
                  </Col>

                  <Col xs={9}>
                    <DatePicker.RangePicker
                      onChange={(e: any) => changeFilter(getDateFromDatePicker(e))}
                    />
                  </Col>
                </Row>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={mapCategories()}
                  pagination={false}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      <Card style={{marginTop: '16px', justifyContent: 'center', display:'flex'}}>
        <Pagination total={countries.totalItemCount} 
          current={filter.page} 
          onChange={(e) => changeFilter({ page: e })} 
          pageSize={filter.limit} 
          showTotal={(total) => `Total ${total} items`} 
          onShowSizeChange={(e, selectedNumber) => {changeFilter({ limit: selectedNumber});}} 
          defaultPageSize={20}
          showSizeChanger />
      </Card>
    </>
  );
}

export default Countries;
